import { FieldRow, AppTextInput } from 'components/FormElements';
import {
  Controller,
  useFormContext,
  useWatch,
  useFieldArray,
} from 'react-hook-form';
import Table from 'components/Table';
import { AppSelectBox } from 'components/FormElements';
import { getFeeAccountingOptions } from 'utils/getFeeAccountingOptions';
import { numberToCurrencyString } from 'further-ui/utils';
import { Delete, InfoOutlined } from '@mui/icons-material';
import Button from 'components/Button';
import {
  Tooltip,
  InputAdornment,
  IconButton,
  useTheme,
  styled,
} from '@mui/material';
import Spacing from 'components/Spacing';
import ButtonGroup from 'components/ButtonGroup';
import { FirmSelector } from 'components/FirmSelector';
import { useGetRole } from 'hooks/ui/useGetRole';
import recalculateCoreFee from './helper/recalculate-core-fee';
import { SelectChangeEvent } from '@mui/material';
import { FeeTemplateFormData } from '.';
import { useCallback } from 'react';
import {
  FeeType,
  FeeCalculationBasis,
  CoreFeeType,
  FeeChargingFrequency,
  FeeAccounting,
} from 'further-types/investment';
import {
  CoreFeeTypeLabels,
  FeeChargingFrequencyLabels,
  FeeCalculationBasisLabels,
} from 'further-ui/labels';
import { useHistory } from 'react-router-dom';
import { CORE_FEE_TEMPLATES } from 'adminConstants/routes';
import { Grid } from '@mui/material';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';

const GrossFeeContainer = styled('div')`
  height: 29px;
  // align with other elements without border
  padding: 9px 12px 9px 0px;
  border-radius: 4px;
`;

type CoreFee = FeeTemplateFormData['coreFees'][number];

const isPercentageFeeType = (feeType: CoreFeeType) => {
  return (
    feeType === CoreFeeType.RecurringPercentage ||
    feeType === CoreFeeType.InitialPercentage
  );
};

const AddEditCoreFeeTemplateForm: React.FC<{
  isLoading: boolean;
  templateInUse?: boolean;
  isEdit?: boolean;
}> = ({ isLoading, templateInUse, isEdit }) => {
  const { control, setValue, getValues, formState } = useFormContext();
  const { isSuperAdmin } = useGetRole();
  const theme = useTheme();
  const history = useHistory();
  const [canCreateCoreFeeTemplate] = useHasPermissions([
    RolePermission.CreateCoreFees,
  ]);
  const inputDisabled = !canCreateCoreFeeTemplate || templateInUse;

  const DISABLED_TOOLTIP_MESSAGE =
    templateInUse && canCreateCoreFeeTemplate
      ? 'It is not possible to edit a core fee template that is already in use.'
      : undefined;

  const { append } = useFieldArray({
    control,
    name: 'coreFees',
  });

  const coreFees = useWatch({
    control,
    name: 'coreFees',
  });

  const firmId = useWatch({
    control,
    name: 'firmId',
  });

  const handleFeeRecalculation = useCallback(
    (e: SelectChangeEvent<unknown>, index: number) => {
      const updatedCoreFees = getValues('coreFees');

      const recalculatedFees = recalculateCoreFee(
        updatedCoreFees,
        e.target?.name?.split('.').pop() || '',
        (e.target?.value ?? '') as string,
        index,
      );

      // Update the values one by one to maintain field references
      recalculatedFees.forEach((fee: CoreFee, i: number) => {
        if (i === index) {
          Object.keys(fee).forEach((key) => {
            setValue(`coreFees.${i}.${key}`, fee[key], {
              shouldDirty: true,
              shouldValidate: !!fee.netFee,
            });
          });
        }
      });
    },
    [getValues, setValue],
  );

  const columns = [
    {
      label: 'Fee name',
      key: 'feeName',
      sort: false,
      render: (_, i) => (
        <Controller
          name={`coreFees.${i}.feeName`}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Tooltip title={DISABLED_TOOLTIP_MESSAGE}>
              <AppTextInput
                {...field}
                disabled={inputDisabled}
                placeholder="Enter fee name"
                fullWidth
                inputRef={field.ref}
                error={!!error}
                helperText={error?.message}
                variant="outlined"
                style={{ minWidth: 200 }}
              />
            </Tooltip>
          )}
        />
      ),
    },
    {
      label: (
        <>
          Fee type
          <Tooltip
            title={
              <>
                <b>Any recurring % fee</b> is charged at the annual rate set out
                in the 'Annual fee %/£ (ex VAT)' column and billed at the
                frequency you set. For example, a 2% fee billed quarterly will
                bill 0.5% a quarter.
                <br />
                <br />
                <b>Any recurring £ fee</b> is charged at the rate set out in the
                'Annual fee %/£ (ex VAT)' column and billed at the frequency you
                set. For example, a £250 fee billed quarterly will bill at £250
                a quarter.
              </>
            }
          >
            <IconButton size="small">
              <InfoOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
        </>
      ),
      key: 'displayFeeType',
      sort: false,
      render: (_, i: number) => (
        <Controller
          name={`coreFees.${i}.displayFeeType`}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Tooltip title={DISABLED_TOOLTIP_MESSAGE}>
              <AppSelectBox
                {...field}
                inputRef={field.ref}
                onChange={(e: SelectChangeEvent<unknown>) => {
                  field.onChange(e);
                  handleFeeRecalculation(e, i);
                }}
                disabled={inputDisabled}
                data={[
                  {
                    label: CoreFeeTypeLabels[CoreFeeType.InitialPercentage],
                    value: CoreFeeType.InitialPercentage,
                  },
                  {
                    label: CoreFeeTypeLabels[CoreFeeType.RecurringPercentage],
                    value: CoreFeeType.RecurringPercentage,
                  },
                  {
                    label: CoreFeeTypeLabels[CoreFeeType.InitialFixed],
                    value: CoreFeeType.InitialFixed,
                  },
                  {
                    label: CoreFeeTypeLabels[CoreFeeType.RecurringFixed],
                    value: CoreFeeType.RecurringFixed,
                  },
                ]}
                valueKey="value"
                labelKey="label"
                variant="outlined"
                error={!!error}
                style={{ minWidth: 120 }}
                helperText={error?.message}
              />
            </Tooltip>
          )}
        />
      ),
    },
    {
      label: 'Charging frequency',
      key: 'chargingFrequency',
      sort: false,
      render: (elm: CoreFee, i: number) => (
        <Controller
          name={`coreFees.${i}.chargingFrequency`}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Tooltip title={DISABLED_TOOLTIP_MESSAGE}>
              <AppSelectBox
                {...field}
                data={[
                  {
                    value: FeeChargingFrequency.Monthly,
                    label:
                      FeeChargingFrequencyLabels[FeeChargingFrequency.Monthly],
                  },
                  {
                    value: FeeChargingFrequency.Quarterly,
                    label:
                      FeeChargingFrequencyLabels[
                        FeeChargingFrequency.Quarterly
                      ],
                  },
                  {
                    value: FeeChargingFrequency.Yearly,
                    label:
                      FeeChargingFrequencyLabels[FeeChargingFrequency.Yearly],
                  },
                ]}
                inputRef={field.ref}
                valueKey="value"
                disabled={elm.feeType === FeeType.Initial || inputDisabled}
                labelKey="label"
                defaultValue={FeeChargingFrequency.Monthly}
                variant="outlined"
                error={!!error}
                helperText={error?.message}
                backgroundColor={
                  elm.feeType === FeeType.Initial
                    ? theme.palette.grey[100]
                    : 'transparent'
                }
              />
            </Tooltip>
          )}
        />
      ),
    },
    {
      label: 'Fee calculation basis',
      key: 'feeCalculationBasis',
      sort: false,
      render: (elm: CoreFee, i: number) => (
        <Controller
          name={`coreFees.${i}.feeCalculationBasis`}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Tooltip title={DISABLED_TOOLTIP_MESSAGE}>
              <AppSelectBox
                {...field}
                value={
                  elm.displayFeeType === CoreFeeType.RecurringPercentage
                    ? field.value
                    : ''
                }
                data={[
                  {
                    value: FeeCalculationBasis.PercentageOfTotalCommitment,
                    label:
                      FeeCalculationBasisLabels[
                        FeeCalculationBasis.PercentageOfTotalCommitment
                      ],
                  },
                  {
                    value: FeeCalculationBasis.PercentageOfUnrealisedAssetCost,
                    label:
                      FeeCalculationBasisLabels[
                        FeeCalculationBasis.PercentageOfUnrealisedAssetCost
                      ],
                    disabled:
                      elm.displayFeeType &&
                      elm.displayFeeType !== CoreFeeType.RecurringPercentage,
                  },
                ]}
                variant="outlined"
                onChange={(e: SelectChangeEvent<unknown>) => {
                  field.onChange(e);
                  handleFeeRecalculation(e, i);
                }}
                labelKey="label"
                valueKey="value"
                error={!!error}
                helperText={error?.message}
                disabled={
                  !elm.displayFeeType ||
                  elm.displayFeeType !== CoreFeeType.RecurringPercentage ||
                  inputDisabled
                }
                backgroundColor={
                  !elm.displayFeeType ||
                  elm.displayFeeType !== CoreFeeType.RecurringPercentage ||
                  inputDisabled
                    ? theme.palette.grey[100]
                    : 'transparent'
                }
                style={{ minWidth: 180 }}
              />
            </Tooltip>
          )}
        />
      ),
    },
    {
      label: (
        <>
          Minimum annual fee %
          <Tooltip
            title={
              <>
                This is the minimum annual fee as a percentage of each
                subscription's Total commitment.
              </>
            }
          >
            <IconButton size="small">
              <InfoOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
        </>
      ),
      key: 'minimumFeePercentage',
      sort: false,
      render: (elm: CoreFee, i: number) => (
        <Controller
          name={`coreFees.${i}.minimumFeePercentage`}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Tooltip title={DISABLED_TOOLTIP_MESSAGE}>
              <AppTextInput
                {...field}
                type="number"
                onChange={(e) => {
                  field.onChange(
                    e.target.value === '' ? '' : Number(e.target.value),
                  );
                }}
                placeholder="e.g. 1"
                error={!!error}
                helperText={error?.message}
                variant="outlined"
                inputRef={field.ref}
                disabled={
                  elm.feeCalculationBasis !==
                    FeeCalculationBasis.PercentageOfUnrealisedAssetCost ||
                  inputDisabled
                }
                backgroundColor={
                  elm.feeCalculationBasis !==
                  FeeCalculationBasis.PercentageOfUnrealisedAssetCost
                    ? theme.palette.grey[100]
                    : 'transparent'
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="start">%</InputAdornment>
                  ),
                }}
              />
            </Tooltip>
          )}
        />
      ),
    },
    {
      label: 'Start period',
      key: 'startingPeriod',
      sort: false,
      render: (elm: CoreFee, i: number) => (
        <Controller
          name={`coreFees.${i}.startingPeriod`}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Tooltip title={DISABLED_TOOLTIP_MESSAGE}>
              <AppTextInput
                {...field}
                type="number"
                inputRef={field.ref}
                placeholder="e.g. 1"
                disabled={elm.feeType === FeeType.Initial || inputDisabled}
                fullWidth
                error={!!error}
                helperText={error?.message}
                variant="outlined"
                onChange={(e) => {
                  field.onChange(
                    e.target.value === '' ? '' : Number(e.target.value),
                  );
                }}
                backgroundColor={
                  elm.feeType === FeeType.Initial
                    ? theme.palette.grey[100]
                    : 'transparent'
                }
              />
            </Tooltip>
          )}
        />
      ),
    },
    {
      label: 'Finish period',
      key: 'finishingPeriod',
      sort: false,
      render: (elm: CoreFee, i: number) => (
        <Controller
          name={`coreFees.${i}.finishingPeriod`}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Tooltip title={DISABLED_TOOLTIP_MESSAGE}>
              <AppTextInput
                {...field}
                inputRef={field.ref}
                type="number"
                placeholder="e.g. 15"
                fullWidth
                disabled={elm.feeType === FeeType.Initial || inputDisabled}
                error={!!error}
                helperText={error?.message}
                variant="outlined"
                onChange={(e) => {
                  field.onChange(
                    e.target.value === '' ? '' : Number(e.target.value),
                  );
                }}
                backgroundColor={
                  elm.feeType === FeeType.Initial
                    ? theme.palette.grey[100]
                    : 'transparent'
                }
              />
            </Tooltip>
          )}
        />
      ),
    },
    {
      label: 'Annual fee %/£ (ex VAT)',
      key: 'netFee',
      sort: false,
      render: (elm: CoreFee, i: number) => {
        const showPercentageSymbol = isPercentageFeeType(elm.displayFeeType);
        return (
          <Controller
            name={`coreFees.${i}.netFee`}
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Tooltip title={DISABLED_TOOLTIP_MESSAGE}>
                <div>
                  <AppTextInput
                    {...field}
                    inputRef={field.ref}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      field.onChange(e);
                      handleFeeRecalculation(e, i);
                    }}
                    disabled={inputDisabled}
                    type="number"
                    placeholder="e.g. 1"
                    error={!!error}
                    helperText={error?.message}
                    variant="outlined"
                    InputProps={{
                      endAdornment: showPercentageSymbol ? (
                        <InputAdornment position="start">%</InputAdornment>
                      ) : null,
                      startAdornment: !showPercentageSymbol ? (
                        <InputAdornment position="start">£</InputAdornment>
                      ) : null,
                    }}
                  />
                </div>
              </Tooltip>
            )}
          />
        );
      },
    },
    {
      label: 'VAT',
      key: 'vat',
      sort: false,
      render: (_, i: number) => (
        <Controller
          name={`coreFees.${i}.vat`}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Tooltip title={DISABLED_TOOLTIP_MESSAGE}>
              <div>
                <AppSelectBox
                  {...field}
                  inputRef={field.ref}
                  onChange={(e: SelectChangeEvent<unknown>) => {
                    field.onChange(e);
                    handleFeeRecalculation(e, i);
                  }}
                  disabled={inputDisabled}
                  data={[
                    { label: '20%', value: 20 },
                    { label: 'No VAT', value: 0 },
                  ]}
                  valueKey="value"
                  labelKey="label"
                  variant="outlined"
                  error={!!error}
                  helperText={error?.message}
                  style={{ minWidth: 80 }}
                  required
                />
              </div>
            </Tooltip>
          )}
        />
      ),
    },
    {
      label: 'Gross fee',
      key: 'grossFee',
      sort: false,
      render: (elm: CoreFee) => {
        const isPercentageFee = isPercentageFeeType(elm.displayFeeType);
        return (
          <Tooltip title={DISABLED_TOOLTIP_MESSAGE}>
            <GrossFeeContainer>
              {!isPercentageFee
                ? numberToCurrencyString(elm?.grossFee)
                : Intl.NumberFormat('en-GB', {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 10,
                  }).format(elm.grossFee) + '%'}
            </GrossFeeContainer>
          </Tooltip>
        );
      },
    },
    {
      label: 'Fee accounting',
      key: 'feeAccounting',
      sort: false,
      render: (elm: CoreFee, i: number) => (
        <Controller
          name={`coreFees.${i}.feeAccounting`}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Tooltip title={DISABLED_TOOLTIP_MESSAGE}>
              <AppSelectBox
                {...field}
                inputRef={field.ref}
                disabled={inputDisabled}
                data={getFeeAccountingOptions({
                  isAccruedDisable: elm.feeType === FeeType.Initial,
                  isChargedToInvestorDisable:
                    elm.feeCalculationBasis ===
                    FeeCalculationBasis.PercentageOfUnrealisedAssetCost,
                })}
                valueKey="value"
                labelKey="label"
                variant="outlined"
                error={!!error}
                helperText={error?.message}
                style={{ minWidth: 170 }}
              />
            </Tooltip>
          )}
        />
      ),
    },
    {
      label: 'Action',
      key: 'action',
      sort: false,
      render: (_, i: number) => (
        <Button
          size="small"
          disabled={inputDisabled}
          tooltip={
            templateInUse && canCreateCoreFeeTemplate
              ? 'It is not possible to delete a core fee template that is currently in use.'
              : undefined
          }
          onClick={() => {
            const updatedCoreFees = coreFees.filter((_, index) => index !== i);
            setValue('coreFees', updatedCoreFees, { shouldDirty: true });
          }}
        >
          <Delete color={inputDisabled ? 'disabled' : 'primary'} />
        </Button>
      ),
    },
  ];

  // Check if the form has any errors or if there are no core fees
  const isFormInvalid =
    Object.keys(formState.errors).length > 0 || !coreFees?.length;

  return (
    <Spacing>
      <Grid container>
        <Grid size={{ md: 6, xs: 12 }}>
          <Spacing>
            {isSuperAdmin && (
              <FieldRow centerTitle title="Firm" narrow>
                <FirmSelector
                  firmId={firmId}
                  disabled={isEdit}
                  onChange={(firmId) => {
                    setValue('firmId', firmId, { shouldValidate: true });
                  }}
                  required={true}
                />
              </FieldRow>
            )}

            <FieldRow title="Fee template name" centerTitle narrow>
              <Controller
                name="feeTemplateName"
                disabled={!canCreateCoreFeeTemplate}
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <AppTextInput
                    {...field}
                    inputRef={field.ref}
                    error={!!error}
                    helperText={error?.message}
                    placeholder="e.g. Standard initial fees, 2% p.a. for 5 years"
                  />
                )}
              />
            </FieldRow>
          </Spacing>
        </Grid>
      </Grid>

      <Table
        columns={columns}
        tablebody={coreFees}
        variant="nohover"
        key={coreFees.length}
        alignRowsTop
      />

      <ButtonGroup>
        <Button
          onClick={() => {
            append({
              feeName: '',
              displayFeeType: '',
              chargingFrequency: '',
              feeBasis: '',
              minimumFeePercentage: 0,
              startingPeriod: '',
              finishingPeriod: '',
              netFee: 0,
              vat: 0,
              feeAccounting: FeeAccounting.ChargedToInvestor,
              grossFee: 0,
            });
          }}
          color={
            templateInUse || !canCreateCoreFeeTemplate ? 'secondary' : 'primary'
          }
          variant="text"
          disabled={templateInUse || !canCreateCoreFeeTemplate}
        >
          <strong>Add fee</strong>
        </Button>
      </ButtonGroup>

      <ButtonGroup>
        {isEdit && (
          <Button
            type="button"
            color="primary"
            variant="outlined"
            onClick={() => history.push(CORE_FEE_TEMPLATES)}
          >
            Cancel
          </Button>
        )}
        {canCreateCoreFeeTemplate && (
          <Button
            type="submit"
            color="primary"
            variant="contained"
            disabled={isFormInvalid || !formState.isDirty}
            loading={isLoading}
          >
            {isEdit ? 'Update fee template' : 'Save fee template'}
          </Button>
        )}
      </ButtonGroup>
    </Spacing>
  );
};

export default AddEditCoreFeeTemplateForm;
